import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { derivePermissions } from '@/access';
import { useRolesQuery, useUserQuery } from '@/api/queries/useAuth';
import { tutorialActiveUnitAtom } from '@/atoms/tutorial';
import { useCompaniesList } from '@/data/entities/companies';
import type { TutorialUnit, UserTutorial } from '@/types';
import { type HubAccess, unitResumable } from './tutorialUnits';

/** The user's grants in the tutorial project, read through their personal
 *  space — whatever workspace they are in now. Before the project exists it
 *  is theirs to create there, as its owner, with every grant. `null` while the
 *  user, roles or companies are still loading: «nothing yet», not «no». */
export function useTutorialAccess(tutorial: UserTutorial | undefined): HubAccess | null {
    const { data: user } = useUserQuery();
    const { data: roles } = useRolesQuery();
    const { data: companies } = useCompaniesList({});
    if (tutorial === undefined) return null;
    const projectId = tutorial.project_id;
    if (projectId === null) return { canAuthorNotes: true };
    if (companies === undefined) return null;
    const companyId = companies.find(company => company.is_personal)?.id;
    const permissions = derivePermissions({ user, roles, companyId, projectId });
    return permissions.resolved ? { canAuthorNotes: permissions.canAuthorNotes } : null;
}

/** The stored active unit, once it may run again: `undefined` while that
 *  waits on the access. A unit the user lost the rights for is dropped — no
 *  partial chapter — silently: its ledger restored, nothing shown or sent. */
export function useResumableUnit(tutorial: UserTutorial | undefined): TutorialUnit | null | undefined {
    const [activeUnit, setActiveUnit] = useAtom(tutorialActiveUnitAtom);
    const access = useTutorialAccess(tutorial);
    const resumable = activeUnit === null ? true : unitResumable(activeUnit, access);
    useEffect(() => {
        if (resumable === false) setActiveUnit(null);
    }, [resumable, setActiveUnit]);
    if (resumable === null) return undefined;
    return resumable ? activeUnit : null;
}
